/**
 * Utility functions for localizing listing fields with fallback logic
 *
 * Fallback priority:
 * 1. User's preferred language (e.g., description_fr for French users)
 * 2. Default field (original/source language)
 * 3. English (description_en) as international fallback
 * 4. Any available language: fr → de → it
 * 5. Empty string if no value exists
 */

import { Listing } from './listing.types'

export type SupportedLanguage = 'en' | 'fr' | 'de' | 'it'

const FALLBACK_LANGUAGES: SupportedLanguage[] = ['en', 'fr', 'de', 'it']

type LocalizableField = 'description' | 'title'

/**
 * Get a localized field value from a listing with fallback logic
 *
 * @param listing - The listing object
 * @param field - The field name ('description' or 'title')
 * @param preferredLanguage - The user's preferred language
 * @returns The localized value or empty string
 */
export function getLocalizedField(
  listing: Listing | null | undefined,
  field: LocalizableField,
  preferredLanguage: SupportedLanguage
): string {
  if (!listing) return ''

  // 1. Try preferred language (e.g., description_fr)
  const preferredKey = `${field}_${preferredLanguage}` as keyof Listing
  const preferredValue = listing[preferredKey]
  if (preferredValue && typeof preferredValue === 'string') {
    return preferredValue
  }

  // 2. Try default field (original/source language)
  const defaultValue = listing[field]
  if (defaultValue && typeof defaultValue === 'string') {
    return defaultValue
  }

  // 3. Try English as international fallback
  if (preferredLanguage !== 'en') {
    const englishKey = `${field}_en` as keyof Listing
    const englishValue = listing[englishKey]
    if (englishValue && typeof englishValue === 'string') {
      return englishValue
    }
  }

  // 4. Try any available language in order: fr → de → it
  for (const lang of FALLBACK_LANGUAGES) {
    if (lang === preferredLanguage || lang === 'en') continue
    const langKey = `${field}_${lang}` as keyof Listing
    const langValue = listing[langKey]
    if (langValue && typeof langValue === 'string') {
      return langValue
    }
  }

  // 5. Return empty string if nothing found
  return ''
}

/**
 * Get the localized description for a listing
 *
 * @param listing - The listing object
 * @param language - The preferred language
 * @returns The localized description or empty string
 */
export function getLocalizedDescription(
  listing: Listing | null | undefined,
  language: SupportedLanguage
): string {
  return getLocalizedField(listing, 'description', language)
}

/**
 * Get the localized title for a listing
 *
 * @param listing - The listing object
 * @param language - The preferred language
 * @returns The localized title or empty string
 */
export function getLocalizedTitle(
  listing: Listing | null | undefined,
  language: SupportedLanguage
): string {
  return getLocalizedField(listing, 'title', language)
}
